/**
 * ImageLightbox - Fullscreen modal for viewing images.
 *
 * Design: DENiZEN
 * - Soft border and shadow around modal container
 * - Rounded close button
 * - High contrast
 * - WCAG 2.1 AA compliant with keyboard navigation
 */

import { useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'

export interface ImageLightboxProps {
  /**
   * Image URL to display
   */
  imageUrl: string
  /**
   * Alt text for the image
   */
  alt?: string
  /**
   * Whether the lightbox is open
   */
  isOpen: boolean
  /**
   * Callback when lightbox is closed
   */
  onClose: () => void
}

export function ImageLightbox({
  imageUrl,
  alt = 'Enlarged image',
  isOpen,
  onClose,
}: ImageLightboxProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const previousActiveElement = useRef<HTMLElement | null>(null)

  // Stable ref for onClose — prevents the keydown handler (and its useEffect)
  // from re-running when the parent passes an unstable inline callback.
  // Without this, every parent re-render creates a new onClose → new handleKeyDown
  // → effect cleanup fires → previousActiveElement.focus() steals focus from
  // whatever the user is interacting with (e.g. a textarea).
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  // Handle escape key — stable because it reads from ref, not from onClose directly
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current()
      }
    },
    []
  )

  // Handle click outside - the modal content has stopPropagation,
  // so any click that reaches here is outside the modal
  const handleBackdropClick = useCallback(() => {
    onCloseRef.current()
  }, [])

  // Add/remove event listeners and manage focus
  useEffect(() => {
    if (!isOpen) return

    // Store the currently focused element to restore later
    previousActiveElement.current = document.activeElement as HTMLElement

    document.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'

    // Focus the close button after render
    requestAnimationFrame(() => {
      closeButtonRef.current?.focus()
    })

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''

      // Restore focus to the element that opened the lightbox
      previousActiveElement.current?.focus()
      previousActiveElement.current = null
    }
  }, [isOpen, handleKeyDown])

  if (!isOpen) return null

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Image preview"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-dark-green/80" />

      {/* Modal content */}
      <div
        className="relative max-w-5xl max-h-[90vh] w-full animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          ref={closeButtonRef}
          type="button"
          onClick={onClose}
          className="absolute -top-4 -right-4 w-12 h-12 border border-dark-green/15 bg-white rounded-full flex items-center justify-center z-10 hover:-translate-y-1 transition-transform focus:outline-none focus-visible:ring-4 focus-visible:ring-mid-green shadow-denizen-sm"
          aria-label="Close image preview"
        >
          <svg
            className="w-6 h-6 text-dark-green"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={3}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Image container */}
        <div
          className="border border-dark-green/15 bg-white overflow-hidden rounded-2xl shadow-denizen"
        >
          <img
            src={imageUrl}
            alt={alt}
            className="w-full h-full max-h-[85vh] object-contain"
          />
        </div>

        {/* Hint text */}
        <p className="text-center text-white font-body text-sm mt-4">
          Press <kbd className="px-2 py-1 bg-white text-dark-green border border-dark-green/15 rounded font-mono">Esc</kbd> or click outside to close
        </p>
      </div>
    </div>,
    document.body
  )
}

export default ImageLightbox
